import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

import { PLUGIN_Component } from "@/PluginEngine";
import mutate from "@/Utils/request/mutate";
import { useEncounter } from "@/pages/Encounters/utils/EncounterProvider";
import { BatchRequestBody } from "@/types/base/batch/batch";
import batchApi from "@/types/base/batch/batchApi";
import {
  EncounterEdit,
  EncounterStatus,
} from "@/types/emr/encounter/encounter";
import encounterApi from "@/types/emr/encounter/encounterApi";
import {
  AppointmentStatus,
  AppointmentUpdateRequest,
} from "@/types/scheduling/schedule";
import scheduleApi from "@/types/scheduling/scheduleApi";
import { TokenStatus, TokenUpdate } from "@/types/tokens/token/token";
import tokenApi from "@/types/tokens/token/tokenApi";

export function MarkEncounterAsCompletedDialog(
  props: React.ComponentProps<typeof AlertDialog>,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedEncounter: encounter } = useEncounter();

  const { mutate: completeEncounter } = useMutation({
    mutationFn: mutate(batchApi.batchRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["encounter", encounter?.id] });
      queryClient.invalidateQueries({
        queryKey: ["appointment", encounter?.appointment?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tokens", encounter?.appointment?.token?.id],
      });
    },
  });

  if (!encounter) return null;

  return (
    <AlertDialog {...props}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("mark_as_complete")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("mark_encounter_as_complete_confirmation")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <PLUGIN_Component
          __name="PatientInfoCardMarkAsComplete"
          encounter={encounter}
        />

        <AlertDialogFooter>
          <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "primary" })}
            onClick={() => {
              const requests: BatchRequestBody<
                AppointmentUpdateRequest | EncounterEdit | TokenUpdate
              >["requests"] = [
                {
                  url: encounterApi.update.path.replace("{id}", encounter.id),
                  method: encounterApi.update.method,
                  reference_id: "encounter-closed",
                  body: {
                    ...encounter,
                    status: EncounterStatus.COMPLETED,
                    encounter_class: encounter.encounter_class,
                    period: {
                      start: encounter.period.start,
                      end: encounter.period.end
                        ? encounter.period.end
                        : new Date().toISOString(),
                    },
                    hospitalization: encounter.hospitalization,
                    priority: encounter.priority,
                    external_identifier: encounter.external_identifier,
                    discharge_summary_advice:
                      encounter.discharge_summary_advice,
                  },
                },
              ];

              if (encounter.appointment) {
                requests.push({
                  url: scheduleApi.appointments.update.path
                    .replace("{facilityId}", encounter.facility.id)
                    .replace("{id}", encounter.appointment.id),
                  method: scheduleApi.appointments.update.method,
                  reference_id: "appointment-closed",
                  body: {
                    status: AppointmentStatus.FULFILLED,
                    note: encounter.appointment.note,
                  },
                });

                if (encounter.appointment.token) {
                  requests.push({
                    url: tokenApi.update.path
                      .replace("{facility_id}", encounter.facility.id)
                      .replace(
                        "{queue_id}",
                        encounter.appointment.token.queue.id,
                      )
                      .replace("{id}", encounter.appointment.token.id),
                    method: tokenApi.update.method,
                    reference_id: "token-closed",
                    body: {
                      note: encounter.appointment.token.note,
                      status: TokenStatus.FULFILLED,
                      sub_queue:
                        encounter.appointment.token.sub_queue?.id || null,
                    },
                  });
                }
              }

              completeEncounter({ requests });
            }}
          >
            {t("mark_as_complete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
